import styled, { css } from 'styled-components';

import type { InterstateTheme } from '@interstate/components/InterstateThemeProvider';

import { compactFieldControlStyles } from '../baseDealActivity/BaseDealActivity.interstate.style';

export const SCENARIO_COLUMN_WIDTH = '27.75rem';
export const SCENARIO_VALUE_WIDTH = '13.5rem';
export const PIN_OFFSET_VAR = '--mv-pin-offset';
export const PIN_CLEARANCE_VAR = '--mv-pin-clearance';

export const ScenarioScroller = styled.div<{ theme: InterstateTheme }>`
    max-width: 100%;
    overflow-x: auto;

    &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.tokens.BaseColorBlue700};
        outline-offset: 2px;
    }
`;

const rowHoverStyles = (rowKeys: readonly string[], theme: InterstateTheme) =>
    rowKeys.map(
        (rowKey) => css`
            &:has([data-row='${rowKey}']:hover) [data-row='${rowKey}'] {
                background-color: ${theme.tokens.BaseColorBlue50};
            }

            &:has([data-row='${rowKey}']:hover) [data-primary='true'] [data-row='${rowKey}'] {
                background-color: ${theme.tokens.BaseColorBlue100};
            }
        `
    );

export const ScenarioGridRoot = styled.div<{
    $columns: number;
    $rowCount: number;
    $hoverRowKeys: readonly string[];
    theme: InterstateTheme;
}>`
    display: grid;
    width: max-content;
    grid-template-columns: repeat(${({ $columns }) => $columns}, ${SCENARIO_COLUMN_WIDTH});
    grid-template-rows: repeat(${({ $rowCount }) => $rowCount}, auto);
    column-gap: 1px;
    background-color: ${({ theme }) => theme.tokens.BaseColorGray300};
    border: 1px solid ${({ theme }) => theme.tokens.BaseColorGray300};
    border-radius: 0.625rem;
    box-shadow: ${({ theme }) => theme.tokens.BaseShadowLg};
    overflow: hidden;

    ${({ $hoverRowKeys, theme }) => rowHoverStyles($hoverRowKeys, theme)}
`;

export const ScenarioColumnRoot = styled.div<{ $isPrimary: boolean; theme: InterstateTheme }>`
    grid-row: 1 / -1;
    display: grid;
    grid-template-rows: subgrid;
    grid-template-columns: minmax(0, 1fr) ${SCENARIO_VALUE_WIDTH};
    column-gap: 0.5rem;
    min-width: 0;
    background-color: ${({ $isPrimary, theme }) =>
        $isPrimary ? theme.tokens.BaseColorBlue50 : theme.tokens.SemColorSurfaceDefault};
    border-top: 3px solid ${({ $isPrimary, theme }) => ($isPrimary ? theme.tokens.BaseColorBlue900 : 'transparent')};
`;

export const ColumnSection = styled.div`
    grid-column: 1 / -1;
    min-width: 0;
    padding: 0.5rem;

    &:empty {
        padding: 0;
    }
`;

export const PinnedColumnSection = styled(ColumnSection)<{ theme: InterstateTheme }>`
    position: relative;
    z-index: 2;
    transform: translateY(var(${PIN_OFFSET_VAR}, 0px));
    background-color: inherit;
    border-bottom: 1px solid ${({ theme }) => theme.tokens.BaseColorGray300};
`;

export const RowRoot = styled.div<{ theme: InterstateTheme }>`
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: subgrid;
    align-items: center;
    min-height: 2.5rem;
    padding: 0.25rem 0.5rem;
    border-bottom: 1px solid ${({ theme }) => theme.tokens.BaseColorGray300};

    .base-deal-activity-control-custom-label {
        margin-bottom: 0 !important;
    }

    * {
        scroll-margin-top: var(${PIN_CLEARANCE_VAR}, 0px);
    }
`;

export const RowLabel = styled.div<{ theme: InterstateTheme }>`
    display: flex;
    align-items: center;
    min-width: 0;
    color: ${({ theme }) => theme.tokens.SemColorOnSurfaceMuted};
`;

const rightAlignedValueInputStyles = css`
    input.MuiInputBase-input:not([role='combobox']) {
        text-align: right;
    }

    .ids-text-input-readonly {
        justify-content: flex-end;
        padding-right: 5px;
    }

    .ids-date-picker-root input.MuiInputBase-input {
        text-align: left;
    }
`;

export const RowValue = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
    min-width: 0;

    ${compactFieldControlStyles}
    ${rightAlignedValueInputStyles}
`;

export const RowLink = styled.span<{ theme: InterstateTheme; $disabled?: boolean }>`
    display: inline-flex;
    align-items: center;
    color: ${({ theme, $disabled }) =>
        $disabled ? theme.tokens.SemColorOnSurfaceInteractiveDisabled : theme.tokens.BaseColorBlue700};
    font-weight: 400;
    text-align: left;
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};

    &:hover {
        text-decoration: ${({ $disabled }) => ($disabled ? 'none' : 'underline')};
    }

    &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.tokens.BaseColorBlue700};
        outline-offset: 2px;
    }
`;

export const ReadOnlyValue = styled.span`
    padding-right: 5px;
    font-size: 14px;
    white-space: nowrap;
`;

export const CompositeGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: center;
    gap: 0.5rem;
    width: 100%;
`;

export const CompositeControlSlot = styled.div`
    grid-column: 1;
    min-width: 0;
`;

export const CompositeValueSlot = styled.div`
    grid-column: 2;
    min-width: 0;
`;

export const MANUAL_EDIT_BAR_WIDTH = '4px';

export const COMPACT_CONTROL_HEIGHT = '24px';

export const ManualEditMarker = styled.div<{ $isMarked: boolean; theme: InterstateTheme }>`
    position: relative;
    width: 100%;
    min-width: 0;

    ${({ $isMarked, theme }) =>
        $isMarked &&
        `
        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            z-index: 1;
            width: ${MANUAL_EDIT_BAR_WIDTH};
            height: ${COMPACT_CONTROL_HEIGHT};
            border-radius: 4px 0 0 4px;
            background-color: ${theme.tokens.SemColorBorderCaution};
            pointer-events: none;
        }
    `}
`;

export const VisuallyHidden = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
`;

export const StackedValue = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    width: 100%;
    min-width: 0;
`;

export const LabelWithInfo = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
`;

export const LabelWithAction = styled.span<{ theme: InterstateTheme }>`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
    white-space: nowrap;
`;

export const LabelSeparator = styled.span<{ theme: InterstateTheme }>`
    width: 1px;
    height: 12px;
    background-color: ${({ theme }) => theme.tokens.BaseColorGray300};
`;

export const SmallAction = styled.span`
    font-size: 12px;
`;

export const OutOfFlowHost = styled.div`
    position: absolute;
    width: 0;
    height: 0;
`;

export const DeferredAmount = styled.span<{ theme: InterstateTheme }>`
    padding-top: 0.25rem;
    font-size: 12px;
    color: ${({ theme }) => theme.tokens.SemColorOnSurfaceSuccessDefault};
`;

export const SubRowLabel = styled.label<{ theme: InterstateTheme }>`
    padding-left: 1rem;
    font-size: 14px;
    color: ${({ theme }) => theme.tokens.SemColorOnSurfaceMuted};
`;

export const SubRowValue = styled.div`
    display: flex;
    justify-content: flex-end;
    min-width: 0;

    ${rightAlignedValueInputStyles}

    > * {
        width: 100%;
    }
`;

export const RowNote = styled.span<{ theme: InterstateTheme }>`
    grid-column: 1 / -1;
    font-size: 14px;
    font-style: italic;
    color: ${({ theme }) => theme.tokens.SemColorOnSurfaceMuted};
`;

export const PaymentGridBand = styled.div`
    grid-column: 1 / -1;
    align-self: start;
    min-width: 0;
    overflow-x: auto;
    padding: 0.25rem 0;

    ${compactFieldControlStyles}

    .payment-grid-item:has(input[name='term-month']) {
        height: auto;
        min-height: 80px;
        align-self: stretch;
    }
`;

export const PaymentGridOffNote = styled(RowNote)`
    align-self: stretch;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const ToggleChevron = styled.span<{ $isExpanded: boolean }>`
    display: inline-flex;
    margin-right: 0.25rem;
    transform: rotate(${({ $isExpanded }) => ($isExpanded ? '90deg' : '0deg')});
    transition: transform 0.2s;
`;

export const SearchSuffix = styled.span<{ theme: InterstateTheme }>`
    display: inline-flex;

    && svg {
        color: ${({ theme }) => theme.tokens.BaseColorBlue700};
    }
`;
